import {forkJoin, isObservable, Observable, of} from "rxjs";
import {map, shareReplay} from "rxjs/operators";
import {AceLoggerService} from "@logging/ace-logger.service";
import {RulesService} from "@app/layout/rules/rulesmanager/rules.service";
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {Task} from "@shared/services/api/task.interface";
import {AceLogger} from "@logging/ace-logger";


export class TaskCache {

  private readonly logger: AceLogger;
  private readonly tasksCache: Map<number, Task | Observable<Map<number, Task>>> = new Map();

  constructor(private rulesResolverService: RulesService,
              logService: AceLoggerService) {
    this.logger = logService.create(TaskCache.name);
  }

  public getTasksByIds(taskIds: number[]): Observable<Map<number, Task>> {
    const uncachedTaskIds: Set<number> = new Set();
    const cachedTasksMap: Map<number, Task> = new Map();
    const taskFetches = new Set<Observable<Map<number, Task>>>();

    taskIds.forEach(taskId => {
      if (this.tasksCache.has(taskId)) {
        const task = this.tasksCache.get(taskId);
        if (isObservable(task)) {
          taskFetches.add(task);
        } else if (task) {
          cachedTasksMap.set(taskId, task);
        }
      } else {
        uncachedTaskIds.add(taskId);
      }
    });

    if (uncachedTaskIds.size) {
      taskFetches.add(this.fetchAndCacheTasksFromAPI(Array.from(uncachedTaskIds)));
    }

    return taskFetches.size
      ? forkJoin(Array.from(taskFetches)).pipe(map((apiResults: Map<number, Task>[]) => {
          apiResults.forEach(tasksById =>
            tasksById.forEach((task, taskId) =>
              cachedTasksMap.set(taskId, task)));
          return cachedTasksMap;
        }))
      : of(cachedTasksMap);
  }

  private fetchAndCacheTasksFromAPI(taskIds: number[]): Observable<Map<number, Task>> {
    // Take a copy of taskIds to ensure the array is not modified by other code while the API request is in flight
    const ids = [...taskIds];
    this.logger.info('Fetching tasks for ids', taskIds);
    const obs = this.rulesResolverService.getTasksById(ids)
      .pipe(
        map((tasks: Task[]) => this.populateTaskCache(tasks, ids)),
        shareReplay(1)
      );
    ids.forEach(siteId => this.tasksCache.set(siteId, obs));
    return obs;
  }

  private populateTaskCache(tasks: Task[], taskIds: number[]): Map<number, Task> {
    const tasksById = CollectionUtils.indexBy(tasks, 'task_id');
    // Loop over all task ids such that if the response does not contain the task details it'll still get
    // cached (as null)
    taskIds.forEach(taskId => {
      this.tasksCache.set(taskId, tasksById.get(taskId));
    });

    return tasksById;
  }
}
